/**
 * User Detail View Component - Detachable Admin Module
 * 
 * This component provides detailed view of a user with credit management and plan management.
 * When extracting to a separate app, this component can be moved with minimal changes.
 */

import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import AddCreditsModal from './AddCreditsModal';
import ChangePlanModal from './ChangePlanModal';
import UpdateLimitsModal from './UpdateLimitsModal';
import { useAdminData } from '../hooks/useAdminData';
import { 
  formatCredits, 
  formatCurrency,
  getPlanInfo, 
  formatUserStatus, 
  getUserDisplayName, 
  formatRelativeTime,
  getCreditTypeInfo,
  formatRemainingTotalCredits
} from '../utils';
import type { PlanType, PlatformUser } from '../types';
import { Avatar, Badge, Button, FormAlert, Modal } from '../../components';
import adminApi, { handleAdminApiError } from '../services/adminApi';

/**
 * Detailed user view with credit management and plan management
 */
export const UserDetailView: React.FC = () => {
  const { userIdentifier: rawUserIdentifier } = useParams<{ userIdentifier: string }>();
  const userIdentifier = rawUserIdentifier ? decodeURIComponent(rawUserIdentifier) : undefined;
  const navigate = useNavigate();
  const { credits, projects, operations, billingConfig } = useAdminData();

  // UI state
  const [activeTab, setActiveTab] = useState<'overview' | 'credits' | 'projects'>('overview');
  const [showAddCredits, setShowAddCredits] = useState(false);
  const [showChangePlan, setShowChangePlan] = useState(false);
  const [showUpdateLimits, setShowUpdateLimits] = useState(false);
  const [showUpgradeConfirm, setShowUpgradeConfirm] = useState(false);
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [upgradeError, setUpgradeError] = useState('');
  const [upgradeMessage, setUpgradeMessage] = useState('');

  useEffect(() => {
    if (userIdentifier) {
      credits.loadCredits(userIdentifier);
      projects.loadProjects(userIdentifier);
    }
  }, [userIdentifier]);

  useEffect(() => {
    billingConfig.loadConfig();
  }, []);

  const handleChangePlanSuccess = () => {
    setShowChangePlan(false);
    credits.refreshCredits();
  };

  const handleUpdateLimitsSuccess = () => {
    setShowUpdateLimits(false);
    credits.refreshCredits();
  };

  const handleUpgradeToUser = async () => {
    const userId = credits.credits?.user?.id;
    if (!userId) return;
    setIsUpgrading(true);
    setUpgradeError('');
    try {
      const result = await adminApi.updateAccountType(userId, 'user');
      setShowUpgradeConfirm(false);
      setUpgradeMessage(result.message);
      credits.refreshCredits();
    } catch (err: any) {
      setUpgradeError(handleAdminApiError(err));
    } finally {
      setIsUpgrading(false);
    }
  };

  if (!userIdentifier) {
    return (
      <AdminLayout>
        <div className="text-center py-bg-v2-6xl">
          <div className="text-text-error-primary text-4xl mb-bg-v2-xl">⚠️</div>
          <h3 className="text-lg font-medium text-text-primary mb-bg-v2-md">User Not Found</h3>
          <p className="text-text-tertiary mb-bg-v2-xl">The requested user could not be found.</p>
          <Link
            to="/admin"
            className="px-bg-v2-xl py-bg-v2-md text-sm font-medium text-text-primary-on-brand bg-bg-brand-solid rounded-bg-v2-lg hover:bg-bg-brand-solid-hover"
          >
            Back to Users
          </Link>
        </div>
      </AdminLayout>
    );
  }

  const user = credits.credits?.user;
  const isGuestAccount = user?.roles?.includes('guest') ?? false;
  const creditSummary = credits.credits?.credit_summary;
  // Separate active and exhausted entries
  const totalCredits = Number(creditSummary?.total_available_credits) || 0;
  const allEntries = credits.credits?.credit_entries || [];
  
  // If total balance is negative, all entries are considered exhausted
  const activeEntries = totalCredits <= 0 ? [] : allEntries.filter(entry => entry.remaining_amount > 0);
  const exhaustedEntries = totalCredits <= 0 ? allEntries : allEntries.filter(entry => entry.remaining_amount <= 0);

  return (
    <AdminLayout>
      <div className="h-full overflow-y-auto scrollbar-hide">
      <div className="space-y-bg-v2-3xl">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Link
            to="/admin"
            className="p-bg-v2-md text-text-quaternary hover:text-text-tertiary rounded-bg-v2-lg hover:bg-bg-secondary"
          >
            ← Back
          </Link>
          
          <div className="flex items-center space-x-bg-v2-lg">
            {isGuestAccount && (
              <Button
                label="Upgrade to Standard user"
                variant="outline"
                size="small"
                onClick={() => { setUpgradeError(''); setShowUpgradeConfirm(true); }}
              />
            )}
            <Button
              label="Add Credits"
              variant="success"
              size="small"
              onClick={() => setShowAddCredits(true)}
            />
            <Button
              label="Change Plan"
              variant="primary"
              size="small"
              className="!bg-utility-purple-600 hover:!bg-utility-purple-700"
              onClick={() => setShowChangePlan(true)}
            />
            {creditSummary && creditSummary.plan_type === 'monthly' && (
              <Button
                label="Update Limits"
                variant="primary"
                size="small"
                onClick={() => setShowUpdateLimits(true)}
              />
            )}
          </div>
        </div>

        {/* Loading State */}
        {credits.loading && (
          <div className="text-center py-bg-v2-6xl">
            <div className="animate-spin rounded-bg-v2-full h-12 w-12 border-b-2 border-border-brand-alt mx-auto"></div>
            <p className="mt-bg-v2-xl text-text-tertiary">Loading user details...</p>
          </div>
        )}

        {upgradeMessage && <FormAlert message={upgradeMessage} variant="success" />}

        {/* Error State */}
        {credits.error && (
          <div className="space-y-bg-v2-md">
            <FormAlert message={credits.error} variant="error" />
            <Button
              label="Try Again"
              variant="link"
              size="small"
              onClick={() => credits.refreshCredits()}
            />
          </div>
        )}

        {/* User Content */}
        {creditSummary && (
          <>
            {/* User Profile Card */}
            <div className="bg-bg-primary rounded-bg-v2-lg shadow-bg-v2-sm p-bg-v2-3xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-bg-v2-xl">
                  {user ? (
                    <Avatar
                      user={{ id: user.id ?? user.email, username: user.username, email: user.email, firstName: user.firstName, lastName: user.lastName }}
                      size="xl"
                    />
                  ) : (
                    <div className="h-16 w-16 rounded-bg-v2-full bg-bg-brand-solid flex items-center justify-center text-text-primary-on-brand text-xl font-medium">
                      U
                    </div>
                  )}
                  <div>
                    <h3 className="text-lg font-semibold text-text-primary">
                      {user ? getUserDisplayName(user) : 'Unknown User'}
                    </h3>
                    {user && (
                      <div className="mt-bg-v2-md flex items-center gap-bg-v2-sm">
                        {(() => {
                          const status = formatUserStatus(user as PlatformUser);
                          return (
                            <span className={`inline-flex items-center px-bg-v2-md py-bg-v2-xxs rounded-bg-v2-full text-xs font-medium ${status.color}`}>
                              {status.text}
                            </span>
                          );
                        })()}
                        {isGuestAccount && <Badge tone="neutral">Guest</Badge>}
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="text-right">
                  <div className={`inline-flex items-center px-bg-v2-lg py-bg-v2-xs rounded-bg-v2-full text-sm font-medium ${
                    getPlanInfo(creditSummary.plan_type).color
                  }`}>
                    {getPlanInfo(creditSummary.plan_type).label}
                  </div>
                  <div className="flex items-center gap-bg-v2-3xl mt-bg-v2-md">
                    <div className="text-right">
                      <p className={`text-2xl font-bold ${
                        (Number(creditSummary?.total_lifetime_usage) || 0) < 0
                          ? 'text-text-error-primary'
                          : 'text-text-primary'
                      }`}>
                        {formatCurrency(Number(creditSummary.total_lifetime_usage) || 0)}
                      </p>
                      <p className="text-sm text-text-tertiary">
                        Lifetime Usage
                      </p>
                    </div>
                    <div className="text-right">
                      <p className={`text-2xl font-bold ${
                        creditSummary?.unlimited_access
                          ? 'text-utility-purple-600'
                          : (Number(creditSummary?.total_available_credits) || 0) < 0
                          ? 'text-text-error-primary'
                          : 'text-text-primary'
                      }`}>
                        {creditSummary?.unlimited_access 
                          ? 'Unlimited' 
                          : formatCurrency(Number(creditSummary.total_available_credits) || 0)
                        }
                      </p>
                      <p className="text-sm text-text-tertiary">
                        {creditSummary?.unlimited_access ? 'Unlimited Access' : 'Total Credits'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="bg-bg-primary rounded-bg-v2-lg shadow-bg-v2-sm">
              <div className="border-b border-border-secondary">
                <nav className="flex space-x-bg-v2-4xl px-bg-v2-3xl">
                  {[
                    { id: 'overview', label: 'Overview' },
                    { id: 'credits', label: 'Credit History' },
                    { id: 'projects', label: 'Projects' }
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id as any)}
                      className={`py-bg-v2-xl px-bg-v2-xs border-b-2 font-medium text-sm ${
                        activeTab === tab.id
                          ? 'border-border-brand-alt text-text-brand-primary'
                          : 'border-transparent text-text-tertiary hover:text-text-secondary hover:border-border-primary'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </nav>
              </div>

              <div className="p-bg-v2-3xl">
                {/* Overview Tab */}
                {activeTab === 'overview' && (
                  <div className="space-y-bg-v2-3xl">
                    {/* Credit Summary Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-bg-v2-xl">
                      {(() => {
                        const allCredits = [
                          { 
                            type: 'free_platform', 
                            amount: Number(creditSummary.free_platform_credits) || 0,
                            label: 'Free Credits'
                          },
                          { 
                            type: 'monthly', 
                            amount: Number(creditSummary.monthly_credits) || 0,
                            label: 'Monthly Credits'
                          },
                          { 
                            type: 'paid_platform', 
                            amount: Number(creditSummary.paid_platform_credits) || 0,
                            label: 'Paid Credits'
                          }
                        ];
                        
                        const planType = creditSummary?.plan_type?.toLowerCase();
                        return allCredits.filter(credit => {
                          if (credit.type === 'free_platform' || credit.type === 'paid_platform') {
                            return true;
                          }
                          if (credit.type === 'monthly') {
                            return planType === 'monthly';
                          }
                          return false;
                        });
                      })().map((credit) => {
                        const typeInfo = getCreditTypeInfo(credit.type);
                        const remainingTotalFormat = formatRemainingTotalCredits(allEntries, credit.type, creditSummary);
                        return (
                          <div key={credit.type} className="bg-bg-secondary rounded-bg-v2-lg p-bg-v2-xl">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm font-medium text-text-tertiary">{credit.label}</p>
                                <p className="text-lg font-semibold text-text-primary">
                                  {remainingTotalFormat}
                                </p>
                                <p className="text-xs text-text-tertiary mt-bg-v2-xs">
                                  Available / Total
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      <div className="bg-bg-secondary rounded-bg-v2-lg p-bg-v2-xl">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-text-tertiary">Lifetime Usage</p>
                            <p className="text-lg font-semibold text-text-primary">
                              {formatCurrency(Number(creditSummary.total_lifetime_usage) || 0)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Plan Information */}
                    <div className="bg-bg-secondary rounded-bg-v2-lg p-bg-v2-xl">
                      <h4 className="text-lg font-semibold text-text-primary mb-bg-v2-xl">Plan Details</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-bg-v2-xl">
                        <div>
                          <p className="text-sm text-text-tertiary">Current Plan</p>
                          <p className="text-lg font-medium text-text-primary">
                            {getPlanInfo(creditSummary.plan_type).label}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-text-tertiary">Monthly Allowance</p>
                          <p className="text-lg font-medium text-text-primary">
                            {formatCredits(Number(creditSummary.monthly_allowance) || 0)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Credits Tab */}
                {activeTab === 'credits' && (
                  <div className="space-y-bg-v2-xl">
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-text-tertiary">
                        {activeEntries.length} active, {' '}
                        {exhaustedEntries.length} exhausted
                      </p>
                    </div>
                    
                    <div className="space-y-bg-v2-xl">
                      {/* Active Entries */}
                      {activeEntries.length > 0 && (
                        <div>
                          <h5 className="text-sm font-medium text-text-primary mb-bg-v2-md">Active Entries</h5>
                          <div className="space-y-bg-v2-md max-h-60 overflow-y-auto">
                            {activeEntries.map((entry) => {
                              const typeInfo = getCreditTypeInfo(entry.type);
                              
                              return (
                                <div key={entry.id} className="border border-border-secondary rounded-bg-v2-lg p-bg-v2-lg hover:bg-bg-secondary transition-colors">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-bg-v2-lg">
                                      <div className="flex-shrink-0">
                                        <div className="relative w-8 h-8 rounded-bg-v2-full flex items-center justify-center">
                                          <div className="absolute inset-0 rounded-bg-v2-full bg-fg-success-primary" style={{ opacity: 0.2 }} />
                                          <div className="relative w-2 h-2 rounded-bg-v2-full bg-fg-success-primary" />
                                        </div>
                                      </div>
                                      <div>
                                        <div className="font-bold text-text-primary text-sm">
                                          {typeInfo.label} Credits
                                        </div>
                                        <div className="text-xs text-text-tertiary mt-bg-v2-xs">
                                          Source: {entry.source === 'admin_grant' ? 'Admin Grant' :
                                                  entry.source === 'monthly_allocation' ? 'Monthly Allocation' :
                                                  entry.source === 'signup_bonus' ? 'Welcome Bonus' :
                                                  entry.source || 'System'}
                                          {entry.description && ` • ${entry.description}`}
                                        </div>
                                        <div className="text-xs text-text-tertiary mt-bg-v2-xs">
                                          Created {formatRelativeTime(entry.created_at)}
                                          {entry.expires_at && ` • Expires ${formatRelativeTime(entry.expires_at)}`}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="text-right">
                                      <div className="text-sm font-semibold text-text-primary">
                                        {formatCurrency(Number(entry.remaining_amount) || 0)}
                                      </div>
                                      <div className="text-xs text-text-tertiary">
                                        of {formatCurrency(Number(entry.original_amount) || 0)}
                                      </div>
                                      {Number(entry.remaining_amount) < Number(entry.original_amount) && (
                                        <div className="text-xs text-text-brand-primary mt-bg-v2-xs">
                                          {Math.round((Number(entry.remaining_amount) / Number(entry.original_amount)) * 100)}% remaining
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Exhausted Entries */}
                      {exhaustedEntries.length > 0 && (
                        <div>
                          <h5 className="text-sm font-medium text-text-primary mb-bg-v2-md">Exhausted Entries</h5>
                          <div className="space-y-bg-v2-md max-h-60 overflow-y-auto">
                            {exhaustedEntries.map((entry) => {
                              const typeInfo = getCreditTypeInfo(entry.type);
                              
                              return (
                                <div key={entry.id} className="border border-border-secondary rounded-bg-v2-lg p-bg-v2-lg hover:bg-bg-secondary transition-colors bg-bg-secondary">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-bg-v2-lg">
                                      <div className="flex-shrink-0">
                                        <div className="relative w-8 h-8 rounded-bg-v2-full flex items-center justify-center">
                                          <div className="absolute inset-0 rounded-bg-v2-full bg-fg-error-primary" style={{ opacity: 0.2 }} />
                                          <div className="relative w-2 h-2 rounded-bg-v2-full bg-fg-error-primary" />
                                        </div>
                                      </div>
                                      <div>
                                        <div className="font-bold text-text-primary text-sm flex items-center">
                                          {typeInfo.label} Credits
                                          <span className="ml-bg-v2-md inline-flex items-center px-bg-v2-md py-bg-v2-xxs rounded-bg-v2-xs text-xs font-medium bg-bg-error-secondary text-text-error-primary">
                                            Exhausted
                                          </span>
                                        </div>
                                        <div className="text-xs text-text-tertiary mt-bg-v2-xs">
                                          Source: {entry.source === 'admin_grant' ? 'Admin Grant' :
                                                  entry.source === 'monthly_allocation' ? 'Monthly Allocation' :
                                                  entry.source === 'signup_bonus' ? 'Welcome Bonus' :
                                                  entry.source || 'System'}
                                          {entry.description && ` • ${entry.description}`}
                                        </div>
                                        <div className="text-xs text-text-tertiary mt-bg-v2-xs">
                                          Created {formatRelativeTime(entry.created_at)}
                                          {entry.expires_at && ` • Expires ${formatRelativeTime(entry.expires_at)}`}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="text-right">
                                      <div className={`text-sm font-semibold ${
                                        entry.remaining_amount < 0 ? 'text-text-error-primary' : 'text-text-tertiary'
                                      }`}>
                                        {/* Show $0.000000 for entries exhausted due to negative total balance */}
                                        {totalCredits <= 0 && entry.remaining_amount > 0 
                                          ? formatCurrency(0) 
                                          : formatCurrency(Number(entry.remaining_amount) || 0)
                                        }
                                      </div>
                                      <div className="text-xs text-text-tertiary">
                                        of {formatCurrency(Number(entry.original_amount) || 0)}
                                      </div>
                                      {entry.remaining_amount < 0 ? (
                                        <div className="text-xs text-text-error-primary mt-bg-v2-xs">
                                          Exhausted
                                        </div>
                                      ) : totalCredits <= 0 && entry.remaining_amount > 0 ? (
                                        <div className="text-xs text-text-error-primary mt-bg-v2-xs">
                                          Exhausted due to negative balance
                                        </div>
                                      ) : (
                                        <div className="text-xs text-text-tertiary mt-bg-v2-xs">
                                          Fully consumed
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* No entries message */}
                      {activeEntries.length === 0 && exhaustedEntries.length === 0 && (
                        <p className="text-center text-text-tertiary py-bg-v2-4xl">No credit entries found</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Projects Tab */}
                {activeTab === 'projects' && (
                  <div className="space-y-bg-v2-xl">
                    {projects.loading ? (
                      <div className="text-center py-bg-v2-4xl">
                        <div className="inline-block animate-spin rounded-bg-v2-full h-8 w-8 border-b-2 border-border-brand-alt"></div>
                        <p className="mt-bg-v2-md text-sm text-text-tertiary">Loading projects...</p>
                      </div>
                    ) : projects.error ? (
                      <div className="text-center py-bg-v2-4xl">
                        <p className="text-text-error-primary">{projects.error}</p>
                      </div>
                    ) : projects.projects && projects.projects.projects.length > 0 ? (
                      <>
                        <div className="flex items-center justify-between mb-bg-v2-xl">
                          <p className="text-sm text-text-tertiary">
                            Total: {projects.projects.total_projects} {projects.projects.total_projects === 1 ? 'project' : 'projects'}
                          </p>
                        </div>

                        <div className="overflow-x-auto">
                          <table className="w-full divide-y divide-border-secondary" style={{tableLayout: 'fixed', width: '100%'}}>
                            <colgroup>
                              <col style={{width: '25%'}} />
                              <col style={{width: '30%'}} />
                              <col style={{width: '12%'}} />
                              <col style={{width: '12%'}} />
                              <col style={{width: '18%'}} />
                              <col style={{width: '12%'}} />
                            </colgroup>
                            <thead className="bg-bg-secondary">
                              <tr>
                                <th scope="col" className="px-bg-v2-lg py-bg-v2-lg text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                                  Project Name
                                </th>
                                <th scope="col" className="px-bg-v2-lg py-bg-v2-lg text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                                  Description
                                </th>
                                <th scope="col" className="px-bg-v2-lg py-bg-v2-lg text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                                  Status
                                </th>
                                <th scope="col" className="px-bg-v2-lg py-bg-v2-lg text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                                  Type
                                </th>
                                <th scope="col" className="px-bg-v2-lg py-bg-v2-lg text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                                  Created Date
                                </th>
                                <th scope="col" className="px-bg-v2-lg py-bg-v2-lg text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                                  Total Cost
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-bg-primary divide-y divide-border-secondary">
                              {projects.projects.projects.map((project) => {
                                const date = new Date(project.created_at * 1000);
                                const formattedDate = `${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}-${date.getFullYear()}`;

                                return (
                                  <tr key={project.id} className="hover:bg-bg-secondary">
                                    <td className="px-bg-v2-lg py-bg-v2-lg">
                                      <div className="text-sm font-medium text-text-primary overflow-hidden whitespace-nowrap" style={{textOverflow: 'ellipsis'}} title={project.title}>
                                        <Link to={`${project.project_type === 'FedRamp' ? '/fedramp/chat' : '/chat'}/${project.id}`} target='_blank'>{project.title}</Link>
                                      </div>
                                    </td>
                                    <td className="px-bg-v2-lg py-bg-v2-lg">
                                      <div className="text-sm text-text-tertiary overflow-hidden whitespace-nowrap" style={{textOverflow: 'ellipsis'}} title={project.description}>
                                        {project.description || '-'}
                                      </div>
                                    </td>
                                    <td className="px-bg-v2-lg py-bg-v2-lg whitespace-nowrap">
                                      <span className={`px-bg-v2-md inline-flex text-xs leading-5 font-semibold rounded-bg-v2-full ${
                                        project?.is_deleted
                                          ? 'bg-bg-error-secondary text-text-error-primary'
                                          : 'bg-bg-success-secondary text-text-success-primary'
                                      }`}>
                                        {project?.is_deleted ? 'Inactive' : 'Active'}
                                      </span>
                                    </td>
                                    <td className="px-bg-v2-lg py-bg-v2-lg whitespace-nowrap">
                                      <span className={`px-bg-v2-md inline-flex text-xs leading-5 font-semibold rounded-bg-v2-full ${
                                        project.project_type === 'Web'
                                          ? 'bg-bg-brand-secondary text-text-brand-secondary'
                                          : 'bg-utility-purple-100 text-text-secondary'
                                      }`}>
                                        {project.project_type}
                                      </span>
                                    </td>
                                    <td className="px-bg-v2-lg py-bg-v2-lg whitespace-nowrap text-sm text-text-tertiary">
                                      {formattedDate}
                                    </td>
                                    <td className="px-bg-v2-lg py-bg-v2-lg whitespace-nowrap text-sm font-medium text-text-primary">
                                      {formatCurrency(project.total_cost)}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-bg-v2-4xl">
                        <p className="text-text-tertiary">No projects found for this user</p>
                      </div>
                    )}
                  </div>
                )}

              </div>
            </div>
          </>
        )}

      </div>
      </div>

      {/* Modals rendered outside the scroll container so position:fixed works correctly */}
        <AddCreditsModal
          isOpen={showAddCredits}
          userIdentifier={userIdentifier}
          userDisplayName={user ? getUserDisplayName(user) : undefined}
          currentBalance={creditSummary ? Number(creditSummary.total_available_credits) : undefined}
          onClose={() => setShowAddCredits(false)}
          onSuccess={() => credits.refreshCredits()}
        />

        <ChangePlanModal
          isOpen={showChangePlan}
          onClose={() => setShowChangePlan(false)}
          onSuccess={handleChangePlanSuccess}
          userIdentifier={userIdentifier || ''}
          currentPlan={(creditSummary?.plan_type as PlanType) || 'starter' as PlanType}
          currentWeeklyAllowance={undefined}
          currentMonthlyAllowance={creditSummary?.monthly_allowance}
          userDisplayName={user ? getUserDisplayName(user) : undefined}
        />

        <Modal
          isOpen={showUpgradeConfirm}
          onClose={() => setShowUpgradeConfirm(false)}
          onCancel={() => setShowUpgradeConfirm(false)}
          onSubmit={handleUpgradeToUser}
          title="Upgrade to Standard user"
          size="sm"
          submitLabel={isUpgrading ? 'Upgrading...' : 'Upgrade'}
          submitLoading={isUpgrading}
          submitDisabled={isUpgrading}
        >
          <div className="space-y-bg-v2-md">
            <p className="text-sm text-text-secondary">
              {user ? getUserDisplayName(user as PlatformUser) : 'This user'} will be signed out and can create
              projects on their next sign-in. Their existing project memberships are kept.
            </p>
            {upgradeError && <FormAlert message={upgradeError} variant="error" />}
          </div>
        </Modal>

        <UpdateLimitsModal
          isOpen={showUpdateLimits}
          onClose={() => setShowUpdateLimits(false)}
          onSuccess={handleUpdateLimitsSuccess}
          userIdentifier={userIdentifier || ''}
          currentPlan={(creditSummary?.plan_type as PlanType) || 'starter' as PlanType}
          currentWeeklyAllowance={undefined}
          currentMonthlyAllowance={creditSummary?.monthly_allowance}
          userDisplayName={user ? getUserDisplayName(user) : undefined}
        />
    </AdminLayout>
  );
};

export default UserDetailView;
